import React, { useState, useRef, useEffect } from 'react';
import { BracketGame } from '../types/types';
import { cn } from '../lib/utils';
import { useConfig } from '../context/ConfigContext';
import { Pencil, Check, X } from 'lucide-react';

interface RegionBracketProps {
  region: string;
  games: BracketGame[];
  direction: 'ltr' | 'rtl';
  renderGame: (game: BracketGame) => React.ReactNode;
  showRoundLabels?: boolean;
}

/**
 * Inline-editable round label. Shows text + pencil icon; click to edit.
 */
const EditableRoundLabel: React.FC<{
  label: string;
  configIndex: number | null; // null = not editable (e.g. Round of 64)
}> = ({ label, configIndex }) => {
  const { updateRoundName } = useConfig();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(label);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setEditValue(label); }, [label]);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (!editValue.trim() || configIndex === null) {
      setEditValue(label);
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await updateRoundName(configIndex, editValue.trim());
    } catch {
      setEditValue(label);
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  if (configIndex === null) {
    return <span>{label}</span>;
  }

  if (isEditing) {
    return (
      <span className="inline-flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') { setEditValue(label); setIsEditing(false); }
          }}
          disabled={isSaving}
          className="bg-transparent border-b border-foreground/30 outline-none text-[9px] uppercase tracking-wider font-medium w-16 text-center text-foreground"
        />
        <button onClick={handleSave} disabled={isSaving} className="text-success hover:text-success/80">
          <Check className="h-2.5 w-2.5" />
        </button>
        <button onClick={() => { setEditValue(label); setIsEditing(false); }} disabled={isSaving} className="text-destructive hover:text-destructive/80">
          <X className="h-2.5 w-2.5" />
        </button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-0.5 group cursor-pointer" onClick={() => setIsEditing(true)}>
      {label}
      <Pencil className="h-2 w-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
    </span>
  );
};

/**
 * Renders a single 16-team regional bracket as 4 horizontal columns
 * (R64 → R32 → S16 → E8) with CSS connector lines between rounds.
 * Supports LTR (left-to-right) and RTL (right-to-left, mirrored) directions.
 */
const RegionBracket: React.FC<RegionBracketProps> = ({ region, games, direction, renderGame, showRoundLabels = false }) => {
  const { roundConfigs } = useConfig();
  const regionGames = games.filter(g => String(g.region) === region);

  const rounds = [
    regionGames.filter(g => g.round === 0).sort((a, b) => a.id - b.id), // 8 games (R64)
    regionGames.filter(g => g.round === 1).sort((a, b) => a.id - b.id), // 4 games (R32)
    regionGames.filter(g => g.round === 2).sort((a, b) => a.id - b.id), // 2 games (S16)
    regionGames.filter(g => g.round === 3).sort((a, b) => a.id - b.id), // 1 game  (E8)
  ];

  // Round labels and their config indices (null = not editable)
  const roundLabelConfigs: { label: string; configIndex: number | null }[] = [
    { label: 'Round of 64', configIndex: null },
    { label: roundConfigs[0]?.name || 'Round of 32', configIndex: 0 },
    { label: roundConfigs[1]?.name || 'Sweet 16', configIndex: 1 },
    { label: roundConfigs[2]?.name || 'Elite 8', configIndex: 2 },
  ];

  // Number of connector bracket pairs between each round
  const connectorPairCounts = [4, 2, 1];
  const isRtl = direction === 'rtl';

  // Minimum widths per round to prevent collapse when games have no teams
  const roundMinWidths = ['min-w-[100px]', 'min-w-[100px]', 'min-w-[90px]', 'min-w-[90px]'];

  return (
    <div className={cn("flex items-stretch", isRtl && "flex-row-reverse")}>
      {rounds.map((roundGames, roundIdx) => (
        <React.Fragment key={roundIdx}>
          {/* Round column */}
          <div className={cn("flex flex-col flex-1", roundMinWidths[roundIdx])}>
            {roundGames.map((game, gameIdx) => (
              <div key={game.id} className="flex-1 flex flex-col justify-center py-[2px]">
                {/* Round label above the first game only */}
                {showRoundLabels && gameIdx === 0 && (
                  <div className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5 px-0.5 text-center">
                    <EditableRoundLabel
                      label={roundLabelConfigs[roundIdx].label}
                      configIndex={roundLabelConfigs[roundIdx].configIndex}
                    />
                  </div>
                )}
                <div className="w-full">
                  {renderGame(game)}
                </div>
              </div>
            ))}
          </div>

          {/* Connector column after this round (except after last round) */}
          {roundIdx < connectorPairCounts.length && (
            <div className="flex flex-col w-5 shrink-0">
              {Array.from({ length: connectorPairCounts[roundIdx] }).map((_, i) => (
                <div key={i} className="flex-1 flex flex-col">
                  <div className={cn(
                    "flex-1",
                    isRtl ? "border-l-2 border-b-2" : "border-r-2 border-b-2",
                    "border-border/40"
                  )} />
                  <div className={cn(
                    "flex-1",
                    isRtl ? "border-l-2 border-t-2" : "border-r-2 border-t-2",
                    "border-border/40"
                  )} />
                </div>
              ))}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default RegionBracket;
